import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { Avatar } from './UI';
import { Filter, Check, ArrowUpDown, MessageSquare, Wifi, WifiOff, CheckCheck } from 'lucide-react';
import { format, isToday, isThisWeek, isThisMonth } from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { FilterModal, TicketFilters, defaultFilters, hasActiveFilters } from './FilterModal';
import { motion, AnimatePresence } from 'motion/react';

type SortKey = 'lastMessage' | 'priority' | 'unread' | 'assignedToMe';
const priorityOrder = { high: 0, medium: 1, low: 2 };

// ── Sort Dropdown ──────────────────────────────────────────────────────
const SortDropdown = ({
  sort, setSort, onClose,
}: {
  sort: SortKey; setSort: (s: SortKey) => void; onClose: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const options: { id: SortKey; label: string }[] = [
    { id: 'lastMessage', label: 'По времени' },
    { id: 'priority', label: 'По приоритету' },
    { id: 'unread', label: 'Сначала непрочитанные' },
    { id: 'assignedToMe', label: 'Мои тикеты' },
  ];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -6 }}
      transition={{ duration: 0.14 }}
      className="absolute right-0 top-full mt-2 bg-white/97 backdrop-blur-2xl rounded-[16px] shadow-[0_8px_40px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.08)] border border-black/8 w-56 z-50 overflow-hidden py-1.5"
    >
      <div className="px-4 py-2 text-[11px] font-semibold text-[#8E8E93] uppercase tracking-widest">Сортировка</div>
      {options.map(opt => (
        <button
          key={opt.id}
          onClick={() => { setSort(opt.id); onClose(); }}
          className="w-full flex items-center justify-between px-4 py-2.5 text-[15px] text-black hover:bg-[#F2F2F7] transition-colors"
        >
          <span>{opt.label}</span>
          {sort === opt.id && (
            <Check size={15} className="text-[#007AFF]" strokeWidth={2.5} />
          )}
        </button>
      ))}
    </motion.div>
  );
};

// ── Ticket Card ───────────────────────────────────────────────────────
const TicketCard = ({
  ticket, user, isSelected, isAssignedToMe,
}: {
  ticket: ReturnType<typeof useStore.getState>['tickets'][0];
  user: ReturnType<typeof useStore.getState>['users'][0] | undefined;
  isSelected: boolean;
  isAssignedToMe: boolean;
}) => {
  const { setActiveTicketId } = useStore();
  const lastMsg = ticket.messages[ticket.messages.length - 1];
  const hasUnread = ticket.status !== 'closed' && !ticket.isRead && ticket.unreadCount > 0;

  const timeLabel = (() => {
    const d = new Date(ticket.lastMessageAt);
    if (isToday(d)) return format(d, 'HH:mm');
    if (isThisWeek(d)) return format(d, 'EEE', { locale: ru });
    return format(d, 'd MMM', { locale: ru });
  })();

  return (
    <button
      onClick={() => setActiveTicketId(ticket.id)}
      className={`w-full text-left rounded-[14px] p-3.5 transition-all duration-200 flex gap-3 relative ${
        isSelected
          ? 'bg-[#007AFF] text-white shadow-[0_4px_20px_rgba(0,122,255,0.4)] scale-[0.99]'
          : 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.07),0_0_0_0.5px_rgba(0,0,0,0.04)] hover:shadow-[0_3px_12px_rgba(0,0,0,0.1)] active:scale-[0.99]'
      }`}
    >
      {/* Avatar */}
      <div className="relative shrink-0 mt-0.5">
        <Avatar
          name={user ? `${user.firstName} ${user.lastName}` : '?'}
          size="40px"
          isOnline={user?.isOnline}
        />
        {isAssignedToMe && (
          <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center shadow ring-2 ${
            isSelected ? 'ring-[#007AFF]' : 'ring-[#F5F5F7]'
          } bg-[#34C759]`}>
            <Check size={8} className="text-white" strokeWidth={3.5} />
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: name + time */}
        <div className="flex justify-between items-baseline mb-0.5">
          <span className={`text-[15px] font-semibold truncate leading-snug ${
            isSelected ? 'text-white' : 'text-black'
          }`}>
            {user?.firstName} {user?.lastName}
          </span>
          <span className={`text-[12px] whitespace-nowrap ml-2 shrink-0 ${
            isSelected ? 'text-white/75' : 'text-[#8E8E93]'
          }`}>
            {timeLabel}
          </span>
        </div>

        {/* Row 2: subject */}
        <div className={`text-[12px] truncate mb-1 ${
          isSelected ? 'text-white/80' : 'text-[#3C3C43]/60'
        }`}>
          {ticket.subject}
        </div>

        {/* Row 3: last message preview */}
        <div className={`text-[13px] truncate ${isSelected ? 'text-white/85' : 'text-[#8E8E93]'}`}>
          {lastMsg?.isInternalNote ? (
            <span className={isSelected ? 'text-[#FFD60A]/90' : 'text-[#FF9500]'}>
              🔒 {lastMsg.text}
            </span>
          ) : (
            lastMsg?.text
          )}
        </div>

        {/* Row 4: tags + unread badge */}
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex gap-1 overflow-hidden">
            {ticket.priority === 'high' && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] uppercase tracking-wider ${
                isSelected ? 'bg-white/20 text-white' : 'bg-[#FF3B30]/10 text-[#FF3B30]'
              }`}>
                Срочно
              </span>
            )}
            {ticket.tags.slice(0, 2).map(tag => (
              <span key={tag} className={`inline-flex items-center px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium uppercase tracking-wider ${
                isSelected ? 'bg-white/20 text-white' : 'bg-black/6 text-[#6E6E73]'
              }`}>
                {tag}
              </span>
            ))}
          </div>

          <div className="ml-2 flex items-center gap-1 shrink-0">
            {hasUnread ? (
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                isSelected ? 'bg-white text-[#007AFF]' : 'bg-[#007AFF] text-white'
              }`}>
                {ticket.unreadCount}
              </span>
            ) : lastMsg?.senderType === 'operator' && (
              <CheckCheck size={14} className={isSelected ? 'text-white/70' : 'text-[#8E8E93]'} />
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

// ── TicketList (main) ──────────────────────────────────────────────────
export const TicketList = () => {
  const { tickets, users, activeTicketId, currentOperatorId, filters, setFilters, isApiOnline, isLoading } = useStore();
  const [statusFilter, setStatusFilter] = useState<'open' | 'pending' | 'closed'>('open');
  const [sort, setSort] = useState<SortKey>('lastMessage');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const sortBtnRef = useRef<HTMLDivElement>(null);

  const counts = {
    open: tickets.filter(t => t.status === 'open').length,
    pending: tickets.filter(t => t.status === 'pending').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };
  const unreadTotal = tickets.filter(t => !t.isRead && t.unreadCount > 0).length;
  const filtersActive = hasActiveFilters(filters);

  const filteredTickets = tickets
    .filter(t => t.status === statusFilter)
    .filter(t => {
      if (filters.priority !== 'all' && t.priority !== filters.priority) return false;
      if (filters.assignment === 'mine' && t.assignedTo !== currentOperatorId) return false;
      if (filters.assignment === 'free' && t.assignedTo != null) return false;
      if (filters.period !== 'all') {
        const d = new Date(t.lastMessageAt);
        if (filters.period === 'today' && !isToday(d)) return false;
        if (filters.period === 'week' && !isThisWeek(d)) return false;
        if (filters.period === 'month' && !isThisMonth(d)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sort === 'lastMessage') return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      if (sort === 'priority') return priorityOrder[a.priority] - priorityOrder[b.priority];
      if (sort === 'unread') {
        if (!a.isRead && b.isRead) return -1;
        if (a.isRead && !b.isRead) return 1;
        return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
      }
      if (sort === 'assignedToMe') {
        const aM = a.assignedTo === currentOperatorId ? 0 : 1;
        const bM = b.assignedTo === currentOperatorId ? 0 : 1;
        return aM - bM;
      }
      return 0;
    });

  const tabLabels: Record<string, string> = {
    open: 'Открытые',
    pending: 'В работе',
    closed: 'Закрытые',
  };

  return (
    <div className="w-full md:w-[400px] h-full flex flex-col bg-[#F5F5F7] border-r border-black/[0.08] flex-shrink-0">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="px-4 pt-5 pb-3 sticky top-0 z-20 bg-[#F5F5F7]/95 backdrop-blur-xl border-b border-black/[0.06]">
        {/* Title row */}
        <div className="flex justify-between items-center mb-3.5">
          <div className="flex items-center gap-2">
            <h2 className="text-[22px] font-bold text-black tracking-tight">Входящие</h2>
            {unreadTotal > 0 && (
              <span className="bg-[#FF3B30] text-white text-[11px] font-bold px-1.5 h-[19px] min-w-[19px] rounded-full flex items-center justify-center">
                {unreadTotal}
              </span>
            )}
            {/* API status dot */}
            <span title={isApiOnline ? 'API онлайн' : 'API офлайн (мок-данные)'}>
              {isApiOnline
                ? <Wifi size={12} className="text-[#34C759]" strokeWidth={2} />
                : <WifiOff size={12} className="text-[#C7C7CC]" strokeWidth={2} />
              }
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1.5">
            {/* Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilterModal(true)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  filtersActive
                    ? 'bg-[#007AFF] text-white shadow-[0_2px_8px_rgba(0,122,255,0.35)]'
                    : 'text-[#007AFF] bg-[#007AFF]/10 hover:bg-[#007AFF]/20'
                }`}
                title="Фильтры"
              >
                <Filter size={15} strokeWidth={2} />
              </button>
              {filtersActive && (
                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#FF3B30] rounded-full ring-2 ring-[#F5F5F7]" />
              )}
            </div>

            {/* Sort */}
            <div className="relative" ref={sortBtnRef}>
              <button
                onClick={() => setShowSortMenu(v => !v)}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  showSortMenu
                    ? 'bg-[#007AFF] text-white'
                    : 'text-[#007AFF] bg-[#007AFF]/10 hover:bg-[#007AFF]/20'
                }`}
                title="Сортировка"
              >
                <ArrowUpDown size={15} strokeWidth={2} />
              </button>
              <AnimatePresence>
                {showSortMenu && (
                  <SortDropdown sort={sort} setSort={setSort} onClose={() => setShowSortMenu(false)} />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Status segmented control */}
        <div className="bg-black/[0.06] p-[3px] rounded-[11px] flex text-[13px] font-medium w-full">
          {(['open', 'pending', 'closed'] as const).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`flex-1 py-[6px] px-1 rounded-[8px] transition-all text-center relative ${
                statusFilter === f
                  ? 'bg-white shadow-[0_1px_4px_rgba(0,0,0,0.12),0_1px_2px_rgba(0,0,0,0.08)] text-black'
                  : 'text-black/55 hover:text-black/80'
              }`}
            >
              {tabLabels[f]}
              {counts[f] > 0 && (
                <span className={`ml-1 text-[10px] font-bold inline-flex items-center justify-center px-1 h-[15px] min-w-[15px] rounded-full ${
                  statusFilter === f ? 'bg-[#007AFF]/12 text-[#007AFF]' : 'bg-black/8 text-black/45'
                }`}>
                  {counts[f]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Active filters chips */}
        <AnimatePresence>
          {filtersActive && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex gap-1.5 pt-2.5 flex-wrap">
                {filters.priority !== 'all' && (
                  <FilterChip
                    label={{ high: 'Высокий', medium: 'Средний', low: 'Низкий' }[filters.priority]}
                    onRemove={() => setFilters({ ...filters, priority: 'all' })}
                  />
                )}
                {filters.assignment !== 'all' && (
                  <FilterChip
                    label={{ mine: 'Мне', free: 'Свободные' }[filters.assignment]}
                    onRemove={() => setFilters({ ...filters, assignment: 'all' })}
                  />
                )}
                {filters.period !== 'all' && (
                  <FilterChip
                    label={{ today: 'Сегодня', week: 'Неделя', month: 'Месяц' }[filters.period as 'today' | 'week' | 'month']}
                    onRemove={() => setFilters({ ...filters, period: 'all' })}
                  />
                )}
                <button
                  onClick={() => setFilters(defaultFilters)}
                  className="text-[11px] text-[#8E8E93] hover:text-[#FF3B30] transition-colors px-1"
                >
                  Сбросить все
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── List ───────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 hide-scrollbar">
        {isLoading ? (
          <SkeletonList />
        ) : filteredTickets.length === 0 ? (
          <EmptyState label={tabLabels[statusFilter]} filtersActive={filtersActive} />
        ) : (
          filteredTickets.map(ticket => {
            const user = users.find(u => u.id === ticket.userId);
            const isAssignedToMe = ticket.assignedTo === currentOperatorId;
            const isSelected = activeTicketId === ticket.id;
            return (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                user={user}
                isSelected={isSelected}
                isAssignedToMe={isAssignedToMe}
              />
            );
          })
        )}
      </div>

      {/* ── Filter Modal ───────────────────────────────────── */}
      <FilterModal
        isOpen={showFilterModal}
        filters={filters}
        onApply={setFilters}
        onClose={() => setShowFilterModal(false)}
      />
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────��─────

const FilterChip = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <button
    onClick={onRemove}
    className="inline-flex items-center gap-1 bg-[#007AFF]/12 text-[#007AFF] text-[11px] font-medium px-2 py-1 rounded-full hover:bg-[#007AFF]/20 transition-colors"
  >
    {label}
    <span className="text-[10px] leading-none">✕</span>
  </button>
);

const EmptyState = ({ label, filtersActive }: { label: string; filtersActive: boolean }) => (
  <div className="flex flex-col items-center justify-center py-16 text-[#8E8E93]">
    <div className="w-16 h-16 bg-[#EBEBF0] rounded-full flex items-center justify-center mb-4">
      <MessageSquare size={24} className="text-[#C7C7CC]" strokeWidth={1.5} />
    </div>
    <p className="text-[15px] font-medium text-[#3C3C43]/60">
      {filtersActive ? 'Нет совпадений' : 'Нет тикетов'}
    </p>
    <p className="text-[13px] mt-1 text-[#C7C7CC]">
      {filtersActive ? 'Попробуйте изменить фильтры' : label.toLowerCase()}
    </p>
  </div>
);

const SkeletonList = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex gap-3 px-1 py-3 animate-pulse">
        <div className="w-10 h-10 bg-black/8 rounded-full shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          <div className="h-[14px] bg-black/8 rounded-full w-2/3" />
          <div className="h-[12px] bg-black/5 rounded-full w-full" />
          <div className="h-[12px] bg-black/5 rounded-full w-1/2" />
        </div>
      </div>
    ))}
  </>
);
